import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Home, Wrench } from "lucide-react";
import { useView, VIEW_TYPES } from "@/context/ViewContext";
import { cn } from "@/lib/utils";

const VIEW_CONFIG = {
  operations: {
    id: "operations",
    labelKey: "views.operations",
    fallbackLabel: "Operations",
    icon: Building2,
    hint: "Operations Hub",
  },
  resident: {
    id: "resident",
    labelKey: "views.resident",
    fallbackLabel: "Resident",
    icon: Home,
    hint: "Resident Portal",
  },
  contractor: {
    id: "contractor",
    labelKey: "views.contractor",
    fallbackLabel: "Contractor",
    icon: Wrench,
    hint: "Field Planner",
  },
};

export function ViewSwitcher({ className }) {
  const { t } = useTranslation("common");
  const { currentView, switchView } = useView();
  const tabsRef = useRef([]);

  const handleKeyDown = (e) => {
    const currentIndex = VIEW_TYPES.indexOf(currentView);
    let nextIndex = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      nextIndex = (currentIndex + 1) % VIEW_TYPES.length;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      nextIndex = (currentIndex - 1 + VIEW_TYPES.length) % VIEW_TYPES.length;
    } else if (e.key === "Home") {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      nextIndex = VIEW_TYPES.length - 1;
    }

    if (nextIndex !== null) {
      const nextViewId = VIEW_TYPES[nextIndex];
      switchView(nextViewId);
      tabsRef.current[nextIndex]?.focus();
    }
  };

  return (
    <div
      className={cn(
        "inline-flex items-center p-1 bg-muted/60 border border-border rounded-xl shadow-xs",
        className
      )}
      role="tablist"
      aria-label={t("views.switcher", "Switch Application View")}
    >
      {VIEW_TYPES.map((viewId, idx) => {
        const config = VIEW_CONFIG[viewId];
        const Icon = config.icon;
        const isActive = currentView === viewId;

        return (
          <button
            key={viewId}
            ref={(el) => {
              tabsRef.current[idx] = el;
            }}
            type="button"
            role="tab"
            tabIndex={isActive ? 0 : -1}
            aria-selected={isActive}
            aria-label={t(config.labelKey, config.fallbackLabel)}
            onClick={() => switchView(viewId)}
            onKeyDown={handleKeyDown}
            title={t(config.labelKey, config.fallbackLabel)}
            className={cn(
              "flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ease-in-out cursor-pointer min-h-[44px] sm:min-h-[36px] select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-muted",
              isActive
                ? "bg-card text-foreground shadow-xs font-bold"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
            )}
          >
            <Icon
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
            />
            <span className="hidden sm:inline">
              {t(config.labelKey, config.fallbackLabel)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default ViewSwitcher;
